"use client";

import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { MediaShareEntry } from "./mediaSourceShare";
const MAX_SOURCES = 50;
const MAX_COPIES = 50;
export const MAX_CAPTURES = 15;
/** Per-image base64 payload limit (chars) to keep shared DynamoDB items within reason. */
export const MAX_CAPTURE_BASE64_CHARS = 280_000;

/** Batch local edits before PUT; reduces API load and avoids overlapping full-item writes. */
const PERSIST_DEBOUNCE_MS = 650;
/** Cap CAS merge retries when many tabs race the same item. */
const MAX_REVISION_CONFLICT_RETRIES = 8;
export type ToolsSharedCaptureEntry = {
  id: string;
  /** Raw base64 only (no `data:` prefix). */
  webpBase64: string;
  createdAt: number;
};
export type ToolsSharedCopyEntry = {
  id: string;
  text: string;
  timestamp: number;
};
export type ToolsSharedNote = {
  id: string;
  title: string;
  content: string;
  createdAt: number;
  updatedAt: number;
};
export type ToolsSharedBundle = {
  captures: ToolsSharedCaptureEntry[];
  sources: MediaShareEntry[];
  copies: ToolsSharedCopyEntry[];
  notes: ToolsSharedNote[];
};
const emptyBundle = (): ToolsSharedBundle => ({
  captures: [],
  sources: [],
  copies: [],
  notes: []
});
export function stripWebpToRawBase64(input: string): string {
  const t = input.trim().replace(/\s/g, "");
  const m = /^data:image\/webp;base64,(.+)$/i.exec(t);
  return m ? m[1]! : t;
}
export function normalizeCapturesList(entries: ToolsSharedCaptureEntry[]): ToolsSharedCaptureEntry[] {
  return entries.map(e => ({
    id: e.id,
    webpBase64: stripWebpToRawBase64(e.webpBase64),
    createdAt: e.createdAt
  })).filter(e => e.webpBase64.length > 0 && e.webpBase64.length <= MAX_CAPTURE_BASE64_CHARS).slice(0, MAX_CAPTURES);
}
function capturesFromUnknown(arr: unknown[]): ToolsSharedCaptureEntry[] {
  const parsed: ToolsSharedCaptureEntry[] = [];
  for (const x of arr) {
    if (!x || typeof x !== "object") continue;
    const o = x as Record<string, unknown>;
    if (typeof o.id !== "string" || typeof o.webpBase64 !== "string" || typeof o.createdAt !== "number") {
      continue;
    }
    parsed.push({
      id: o.id,
      webpBase64: o.webpBase64,
      createdAt: o.createdAt
    });
  }
  return normalizeCapturesList(parsed);
}
function normalizeBundle(raw: Partial<ToolsSharedBundle> | null | undefined): ToolsSharedBundle {
  const b = emptyBundle();
  if (!raw || typeof raw !== "object") return b;
  if (Array.isArray(raw.captures)) b.captures = capturesFromUnknown(raw.captures as unknown[]);
  if (Array.isArray(raw.sources)) b.sources = raw.sources.slice(0, MAX_SOURCES);
  if (Array.isArray(raw.copies)) b.copies = raw.copies.slice(0, MAX_COPIES);
  if (Array.isArray(raw.notes)) b.notes = raw.notes;
  return b;
}
function parseRevision(raw: unknown): number {
  if (!raw || typeof raw !== "object") return 0;
  const r = (raw as Record<string, unknown>).revision;
  if (typeof r === "number" && Number.isInteger(r) && r >= 0) {
    return r;
  }
  return 0;
}

/** Union by `id`; on duplicate keep the entry with the higher score (newer). */
function mergeByIdPreferNewer<T extends {
  id: string;
}>(local: T[], server: T[], score: (t: T) => number): T[] {
  const map = new Map<string, T>();
  for (const x of server) {
    map.set(x.id, x);
  }
  for (const x of local) {
    const existing = map.get(x.id);
    if (!existing || score(x) >= score(existing)) {
      map.set(x.id, x);
    }
  }
  return Array.from(map.values()).sort((a, b) => score(b) - score(a));
}
function mergeBundles(local: ToolsSharedBundle, server: ToolsSharedBundle): ToolsSharedBundle {
  const captures = mergeByIdPreferNewer(local.captures, server.captures, c => c.createdAt);
  const copies = mergeByIdPreferNewer(local.copies, server.copies, c => c.timestamp);
  const notes = mergeByIdPreferNewer(local.notes, server.notes, n => n.updatedAt);
  const sources = mergeByIdPreferNewer(local.sources, server.sources, s => s.timestamp);
  return normalizeBundle({
    captures,
    copies,
    notes,
    sources
  });
}
export async function getSharedData(caseId: string, dataType: string = "all") {
  const response = await fetch(`/api/protected/sharedData`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      caseId,
      action: "get",
      dataType
    })
  });
  if (!response.ok) {
    const t = await response.text();
    throw new Error(t || response.statusText);
  }
  return response.json() as Promise<Partial<ToolsSharedBundle>>;
}
export type UpdateSharedDataResult = {
  ok: true;
  revision: number;
} | {
  ok: false;
  conflict: true;
  serverRevision: number;
  serverBundle: ToolsSharedBundle;
};
export async function updateSharedData(caseId: string, bundle: ToolsSharedBundle, expectedRevision: number): Promise<UpdateSharedDataResult> {
  const response = await fetch(`/api/protected/sharedData`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      caseId,
      action: "update",
      revision: expectedRevision,
      captures: bundle.captures,
      copies: bundle.copies,
      notes: bundle.notes,
      sources: bundle.sources
    })
  });
  if (response.status === 409) {
    let body: Record<string, unknown> = {};
    try {
      body = (await response.json()) as Record<string, unknown>;
    } catch {
      /* empty body */
    }
    const serverRevision = parseRevision(body);
    return {
      ok: false,
      conflict: true,
      serverRevision,
      serverBundle: normalizeBundle(body as Partial<ToolsSharedBundle>)
    };
  }
  if (!response.ok) {
    const t = await response.text();
    throw new Error(t || response.statusText);
  }
  let data: Record<string, unknown> = {};
  try {
    data = (await response.json()) as Record<string, unknown>;
  } catch {
    /* ignore */
  }
  const rev = data.revision;
  const revision = typeof rev === "number" && Number.isInteger(rev) && rev >= 0 ? rev : expectedRevision + 1;
  return {
    ok: true,
    revision
  };
}
export type ToolsSharedCaseContextValue = {
  caseId: string;
  hydrated: boolean;
  captures: ToolsSharedCaptureEntry[];
  setCaptures: React.Dispatch<React.SetStateAction<ToolsSharedCaptureEntry[]>>;
  sources: MediaShareEntry[];
  setSources: React.Dispatch<React.SetStateAction<MediaShareEntry[]>>;
  copies: ToolsSharedCopyEntry[];
  setCopies: React.Dispatch<React.SetStateAction<ToolsSharedCopyEntry[]>>;
  notes: ToolsSharedNote[];
  setNotes: React.Dispatch<React.SetStateAction<ToolsSharedNote[]>>;
  /** Re-fetch the case bundle from the server and replace in-memory state (skips a redundant persist round-trip). */
  refetch: () => Promise<void>;
};
const ToolsSharedCaseDataContext = createContext<ToolsSharedCaseContextValue | null>(null);
export function useOptionalToolsSharedCaseData() {
  return useContext(ToolsSharedCaseDataContext);
}

/**
 * Shared state + effects for a case-bound bundle. When `caseId` is falsy, returns `null` and
 * resets internal state so the parent can always render a single `Context.Provider` root — avoiding
 * swapping `Provider` vs another component type, which would remount all descendants.
 */
function useToolsSharedCaseContextValue(caseId: string | undefined): ToolsSharedCaseContextValue | null {
  const [hydrated, setHydrated] = useState(false);
  const [bundle, setBundle] = useState<ToolsSharedBundle>(emptyBundle);
  const skipNextPersist = useRef(true);
  const bundleRef = useRef(bundle);
  const bundleVersionRef = useRef(0);
  /** Last known DynamoDB `revision` from GET / successful PUT (optimistic concurrency). */
  const bundleRevisionRef = useRef(0);
  /** Per-case monotonic seq so a flush for case A is not invalidated when case B hydrates and resets global state. */
  const persistSeqByCaseRef = useRef<Map<string, number>>(new Map());
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Active `caseId` for this render; used so a late async flush for a previous case does not stomp refs/state. */
  const currentCaseIdRef = useRef<string | undefined>(caseId);
  currentCaseIdRef.current = caseId;
  bundleRef.current = bundle;
  const fetchAndApplyBundle = useCallback(async () => {
    if (!caseId) return;
    const raw = await getSharedData(caseId);
    skipNextPersist.current = true;
    bundleRevisionRef.current = parseRevision(raw);
    setBundle(normalizeBundle(raw));
  }, [caseId]);
  const refetch = useCallback(async () => {
    if (!caseId) return;
    try {
      await fetchAndApplyBundle();
    } catch (e) {
      console.error("getSharedData failed", e);
    }
  }, [caseId, fetchAndApplyBundle]);
  useEffect(() => {
    if (!caseId) {
      skipNextPersist.current = true;
      bundleVersionRef.current = 0;
      bundleRevisionRef.current = 0;
      setHydrated(false);
      setBundle(emptyBundle());
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
      return;
    }
    let cancelled = false;
    skipNextPersist.current = true;
    // Reset persist seq only for this case so an in-flight flush for another caseId still completes.
    persistSeqByCaseRef.current.delete(caseId);
    bundleVersionRef.current = 0;
    bundleRevisionRef.current = 0;
    setHydrated(false);
    setBundle(emptyBundle());
    (async () => {
      try {
        const raw = await getSharedData(caseId);
        if (cancelled) return;
        bundleRevisionRef.current = parseRevision(raw);
        setBundle(normalizeBundle(raw));
      } catch (e) {
        console.error("getSharedData failed", e);
        if (!cancelled) setBundle(emptyBundle());
      } finally {
        if (!cancelled) {
          setHydrated(true);
          skipNextPersist.current = true;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);
  const flushPersist = useCallback(async (caseIdForWrite?: string, frozen?: {
    snapshot: ToolsSharedBundle;
    revision: number;
  }) => {
    const cid = caseIdForWrite ?? caseId;
    if (!cid) return;
    const useFrozen = !!frozen;
    let snapshot = frozen?.snapshot ?? bundleRef.current;
    let expectedRevision = frozen?.revision ?? bundleRevisionRef.current;
    // One or more PUTs until no local edits landed during the last in-flight request.
    // Per-case seq drops completions only when a newer persist for the *same* case started — not when
    // another case hydrates (which used to reset a global gen and lose the previous case's flush).
    // `revision` + server merge on 409 prevents silent cross-tab data loss.
    let conflictRetries = 0;
    for (;;) {
      const seqMap = persistSeqByCaseRef.current;
      const prevSeq = seqMap.get(cid) ?? 0;
      const mySeq = prevSeq + 1;
      seqMap.set(cid, mySeq);
      const versionAtStart = bundleVersionRef.current;
      let result: UpdateSharedDataResult;
      try {
        result = await updateSharedData(cid, snapshot, expectedRevision);
      } catch (e) {
        if (seqMap.get(cid) !== mySeq) return;
        console.error("updateSharedData failed", e);
        return;
      }
      if (seqMap.get(cid) !== mySeq) return;
      if (!result.ok) {
        if (conflictRetries++ >= MAX_REVISION_CONFLICT_RETRIES) {
          console.error("updateSharedData: too many revision conflicts, giving up");
          return;
        }
        snapshot = mergeBundles(snapshot, result.serverBundle);
        expectedRevision = result.serverRevision;
        if (cid === currentCaseIdRef.current) {
          bundleRevisionRef.current = result.serverRevision;
          bundleRef.current = snapshot;
          setBundle(snapshot);
        }
        continue;
      }
      conflictRetries = 0;
      if (cid === currentCaseIdRef.current) {
        bundleRevisionRef.current = result.revision;
      }
      if (useFrozen) {
        break;
      }
      if (cid !== currentCaseIdRef.current) {
        break;
      }
      if (bundleVersionRef.current === versionAtStart) {
        break;
      }
      snapshot = bundleRef.current;
      expectedRevision = bundleRevisionRef.current;
    }
  }, [caseId]);
  useEffect(() => {
    bundleVersionRef.current += 1;
  }, [bundle]);
  useEffect(() => {
    if (!caseId || !hydrated) return;
    if (skipNextPersist.current) {
      skipNextPersist.current = false;
      return;
    }
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      persistTimerRef.current = null;
      void flushPersist();
    }, PERSIST_DEBOUNCE_MS);
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
    };
  }, [bundle, hydrated, caseId, flushPersist]);

  // Flush pending edits on case switch or unmount only — not on every `bundle` change (the persist
  // effect above re-runs on each edit; its cleanup must not call flush or debounce is defeated).
  // `useLayoutEffect`: cleanup runs before passive `useEffect` (hydration). If we used `useEffect`
  // here, `void flushPersist()` would defer reading `bundleRef` to a microtask; hydration could
  // `setBundle(emptyBundle())` and a re-render would point `bundleRef` at the new case's empty
  // bundle before the flush ran — wiping the previous case in DynamoDB.
  useLayoutEffect(() => {
    const id = caseId;
    return () => {
      if (!id) return;
      const snapshot = bundleRef.current;
      const revision = bundleRevisionRef.current;
      void flushPersist(id, {
        snapshot,
        revision
      });
    };
  }, [caseId, flushPersist]);
  const setCaptures = useCallback((action: React.SetStateAction<ToolsSharedCaptureEntry[]>) => {
    setBundle(prev => {
      const next = typeof action === "function" ? (action as (c: ToolsSharedCaptureEntry[]) => ToolsSharedCaptureEntry[])(prev.captures) : action;
      return {
        ...prev,
        captures: normalizeCapturesList(next)
      };
    });
  }, []);
  const setSources = useCallback((action: React.SetStateAction<MediaShareEntry[]>) => {
    setBundle(prev => {
      const next = typeof action === "function" ? (action as (s: MediaShareEntry[]) => MediaShareEntry[])(prev.sources) : action;
      return {
        ...prev,
        sources: next.slice(0, MAX_SOURCES)
      };
    });
  }, []);
  const setCopies = useCallback((action: React.SetStateAction<ToolsSharedCopyEntry[]>) => {
    setBundle(prev => {
      const next = typeof action === "function" ? (action as (c: ToolsSharedCopyEntry[]) => ToolsSharedCopyEntry[])(prev.copies) : action;
      return {
        ...prev,
        copies: next.slice(0, MAX_COPIES)
      };
    });
  }, []);
  const setNotes = useCallback((action: React.SetStateAction<ToolsSharedNote[]>) => {
    setBundle(prev => ({
      ...prev,
      notes: typeof action === "function" ? (action as (n: ToolsSharedNote[]) => ToolsSharedNote[])(prev.notes) : action
    }));
  }, []);
  return useMemo((): ToolsSharedCaseContextValue | null => {
    if (!caseId) return null;
    return {
      caseId,
      hydrated,
      captures: bundle.captures,
      setCaptures,
      sources: bundle.sources,
      setSources,
      copies: bundle.copies,
      setCopies,
      notes: bundle.notes,
      setNotes,
      refetch
    };
  }, [caseId, hydrated, bundle.captures, bundle.sources, bundle.copies, bundle.notes, setCaptures, setSources, setCopies, setNotes, refetch]);
}
export function ToolsSharedCaseDataProvider({
  caseId,
  children
}: {
  caseId?: string;
  children: React.ReactNode;
}) {
  const value = useToolsSharedCaseContextValue(caseId);
  return <ToolsSharedCaseDataContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="ToolsSharedCaseDataProvider" data-sentry-source-file="tools-shared-case-data.tsx">
      {children}
    </ToolsSharedCaseDataContext.Provider>;
}